import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Card,
  CardMedia,
  CardActions,
} from '@mui/material';
import {
  Save as SaveIcon,
  Preview as PreviewIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import axios from 'axios';

const EmailTemplate = () => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [formData, setFormData] = useState({
    subject: '',
    body_html: '',
    body_text: '',
    weeknight_send_time: '19:30',
    weekend_send_time: '08:30',
  });
  const [attachmentFilename, setAttachmentFilename] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [testEmailData, setTestEmailData] = useState({ recipient_name: '', recipient_email: '' });
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    loadTemplate();
  }, []);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/email/template');
      setTemplate(response.data);
      setFormData({
        subject: response.data.subject,
        body_html: response.data.body_html,
        body_text: response.data.body_text || '',
        weeknight_send_time: response.data.weeknight_send_time || '19:30',
        weekend_send_time: response.data.weekend_send_time || '08:30',
      });
      setAttachmentFilename(response.data.attachment_filename || null);
      if (response.data.attachment_filename) {
        // Load preview
        loadImagePreview();
      }
    } catch (err) {
      setError('Failed to load email template');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      const response = await axios.put('/email/template', formData);
      setTemplate(response.data);
      setSuccess('Template saved successfully');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const loadImagePreview = async () => {
    try {
      const response = await axios.get('/email/template/image', {
        responseType: 'blob',
      });
      const imageUrl = URL.createObjectURL(response.data);
      setAttachmentPreview(imageUrl);
    } catch (err) {
      // Image might not exist, that's okay
      setAttachmentPreview(null);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload a JPG, PNG, GIF, or WebP image.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size too large. Maximum size is 5MB.');
      return;
    }

    try {
      setUploading(true);
      setError('');
      
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post('/email/template/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setAttachmentFilename(response.data.filename);
      setSuccess('Image uploaded successfully');
      setTimeout(() => setSuccess(''), 5000);
      
      // Load preview
      await loadImagePreview();
      
      // Reload template to get updated attachment_path
      await loadTemplate();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleRemoveImage = async () => {
    try {
      setUploading(true);
      setError('');
      
      await axios.delete('/email/template/remove-image');
      
      setAttachmentFilename(null);
      setAttachmentPreview(null);
      setSuccess('Image removed successfully');
      setTimeout(() => setSuccess(''), 5000);
      
      // Reload template
      await loadTemplate();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove image');
    } finally {
      setUploading(false);
    }
  };

  const handlePreview = async () => {
    try {
      setPreviewLoading(true);
      const response = await axios.post('/email/preview', {
        guest_name: 'John Smith',
        tour_name: 'Night Kayak Tour',
        tour_date: '15 December 2025',
        tour_time: '07:00 PM',
        available_seats: 3,
      });
      setPreview(response.data);
      setPreviewOpen(true);
    } catch (err) {
      setError('Failed to generate preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailData.recipient_name || !testEmailData.recipient_email) {
      setError('Please enter both recipient name and email address');
      return;
    }

    try {
      setSendingTest(true);
      setError('');
      
      const response = await axios.post('/email/template/send-test', {
        recipient_name: testEmailData.recipient_name,
        recipient_email: testEmailData.recipient_email,
      });
      
      setSuccess(response.data.message || 'Test email sent successfully');
      setTimeout(() => setSuccess(''), 5000);
      setTestEmailOpen(false);
      setTestEmailData({ recipient_name: '', recipient_email: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Email Template Editor</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<PreviewIcon />}
                onClick={handlePreview}
                disabled={previewLoading}
              >
                Preview
              </Button>
              <Button
                variant="outlined"
                startIcon={<SendIcon />}
                onClick={() => setTestEmailOpen(true)}
                disabled={sendingTest}
              >
                Send Test Email
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                Save Template
              </Button>
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="Invite your friends & family – spots still available! ✨"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={15}
                label="HTML Body"
                value={formData.body_html}
                onChange={(e) => setFormData({ ...formData, body_html: e.target.value })}
                placeholder="Hi {{ guest_name }}, ..."
                helperText="Available variables: {{ guest_name }}, {{ tour_name }}, {{ tour_date }}, {{ tour_time }}, {{ available_seats }}"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={10}
                label="Plain Text Body (Optional)"
                value={formData.body_text}
                onChange={(e) => setFormData({ ...formData, body_text: e.target.value })}
                placeholder="Plain text version of the email..."
                helperText="If not provided, HTML will be used as fallback"
              />
            </Grid>
            
            {/* Send Time Configuration */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Email Send Schedule (AEDT/AEST)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="time"
                      label="Weeknight Send Time"
                      value={formData.weeknight_send_time}
                      onChange={(e) => setFormData({ ...formData, weeknight_send_time: e.target.value })}
                      helperText="Time to send emails on weekdays (Mon-Fri)"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="time"
                      label="Weekend Send Time"
                      value={formData.weekend_send_time}
                      onChange={(e) => setFormData({ ...formData, weekend_send_time: e.target.value })}
                      helperText="Time to send emails on weekends (Sat-Sun)"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            {/* Image Attachment Section */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ImageIcon /> Email Attachment Image
                </Typography>
                
                {attachmentFilename ? (
                  <Box>
                    <Card sx={{ maxWidth: 400, mb: 2 }}>
                      {attachmentPreview && (
                        <CardMedia
                          component="img"
                          height="200"
                          image={attachmentPreview}
                          alt="Email attachment preview"
                          sx={{ objectFit: 'contain' }}
                        />
                      )}
                      <CardActions>
                        <Typography variant="body2" sx={{ flexGrow: 1, px: 1 }}>
                          {attachmentFilename}
                        </Typography>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={handleRemoveImage}
                          disabled={uploading}
                        >
                          Remove
                        </Button>
                      </CardActions>
                    </Card>
                    <Typography variant="caption" color="text.secondary">
                      This image will be attached to all emails sent by the campaign.
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <input
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      style={{ display: 'none' }}
                      id="image-upload-input"
                      type="file"
                      onChange={handleImageUpload}
                    />
                    <label htmlFor="image-upload-input">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<CloudUploadIcon />}
                        disabled={uploading}
                        fullWidth
                        sx={{ mb: 1 }}
                      >
                        {uploading ? 'Uploading...' : 'Upload Image'}
                      </Button>
                    </label>
                    <Typography variant="caption" color="text.secondary">
                      Upload an image to attach to all campaign emails. Supported formats: JPG, PNG, GIF, WebP (max 5MB).
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>

          {template && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Template Version: {template.version || '1.0'} | Last Updated: {new Date(template.updated_at).toLocaleString()}
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Email Preview
          <IconButton
            aria-label="close"
            onClick={() => setPreviewOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {preview && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Subject:</Typography>
              <Typography variant="body1" sx={{ mb: 3, p: 1, bgcolor: 'grey.100' }}>
                {preview.subject}
              </Typography>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Body:</Typography>
              <Box
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                }}
                dangerouslySetInnerHTML={{ __html: preview.body_html }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={testEmailOpen} onClose={() => setTestEmailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Send Test Email
          <IconButton
            aria-label="close"
            onClick={() => setTestEmailOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Recipient Name"
              value={testEmailData.recipient_name}
              onChange={(e) => setTestEmailData({ ...testEmailData, recipient_name: e.target.value })}
              placeholder="John"
              sx={{ mb: 2 }}
              helperText="This will replace {{ guest_name }} in the email"
            />
            <TextField
              fullWidth
              label="Recipient Email"
              type="email"
              value={testEmailData.recipient_email}
              onChange={(e) => setTestEmailData({ ...testEmailData, recipient_email: e.target.value })}
              placeholder="john@example.com"
              helperText="The email address to send the test email to"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestEmailOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSendTestEmail}
            disabled={sendingTest || !testEmailData.recipient_name || !testEmailData.recipient_email}
          >
            {sendingTest ? 'Sending...' : 'Send Test Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailTemplate;

